import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col, Card, Button, Form, Spinner, Table, Badge } from "react-bootstrap";
import Select from "react-select";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import SignatureModal from "../Common/SignatureModal";

const num = (v) => { const n = Number(v); return isFinite(n) ? n : 0; };

const calcQty = (m) =>
 Number((num(m.nos || 1) * num(m.length) * (num(m.breadth) || 1) * (num(m.height) || 1)).toFixed(4));

const getItemKey = (item) =>
 [item.itemName || "", item.unit || "", item.description || ""]
 .map((v) => String(v).trim().toLowerCase())
 .filter(Boolean)
 .join("|");

const emptyMeasurement = () => ({ location: "", nos: 1, length: 0, breadth: 0, height: 0, remarks: "" });

const VendorJMREdit = () => {
 const navigate = useNavigate();
 const { id } = useParams();

 const [existingJMR, setExistingJMR] = useState(null);
 const [selectedVWO, setSelectedVWO] = useState(null);
 const [otherJMRs, setOtherJMRs] = useState([]);
 const [availableItems, setAvailableItems] = useState([]);
 const [selectedItemKeys, setSelectedItemKeys] = useState([]);
 const [items, setItems] = useState([]);

 const [measurementDate, setMeasurementDate] = useState("");
 const [remarks, setRemarks] = useState("");
 const [jmrFile, setJmrFile] = useState(null);

 const [showSignModal, setShowSignModal] = useState(false);
 const [pendingFormData, setPendingFormData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);

 // Load existing JMR 
 useEffect(() => {
 const load = async () => {
 try {
 setLoading(true);
 const res = await api.get(`/vendor-jmrs/${id}`);
 if (!res.data.success) throw new Error("Not found");
 const jmr = res.data.data;
 setExistingJMR(jmr);
 setMeasurementDate(jmr.measurementDate ? new Date(jmr.measurementDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
 setRemarks(jmr.remarks || "");

 const vwoId = typeof jmr.vendorWorkOrder === "object" ? jmr.vendorWorkOrder._id : jmr.vendorWorkOrder;
 const [vwoRes, jmrsRes] = await Promise.all([
 api.get(`/vendor-work-orders/${vwoId}`),
 api.get(`/vendor-jmrs?vendorWorkOrder=${vwoId}&limit=1000`),
 ]);
 if (vwoRes.data.success) setSelectedVWO(vwoRes.data.data);
 setOtherJMRs((jmrsRes.data.data || []).filter((j) => j._id !== id && j.approvalStatus !== "Rejected"));
 } catch {
 swal("Error", "Failed to load Vendor JMR", "error");
 navigate("/vendor-jmr");
 } finally { setLoading(false); }
 };
 load();
 }, [id]);

 const measuredQuantityMap = useMemo(() => {
 const map = new Map();
 otherJMRs.forEach((j) => {
 (j.items || []).forEach((item) => {
 const key = getItemKey(item);
 map.set(key, num(map.get(key)) + num(item.measuredQuantity));
 });
 });
 return map;
 }, [otherJMRs]);

 // Build items from VWO + pre-fill from existing JMR
 useEffect(() => {
 if (!selectedVWO || !existingJMR) return;
 const currentJMRItemMap = new Map((existingJMR.items || []).map((item) => [getItemKey(item), item]));

 const allItems = (selectedVWO.items || []).map((woItem) => {
 const woQty = num(woItem.quantity);
 const prevQty = num(measuredQuantityMap.get(getItemKey(woItem)));
 const existingItem = currentJMRItemMap.get(getItemKey(woItem));
 const currentMeasured = num(existingItem?.measuredQuantity);
 const remaining = Math.max(woQty - prevQty, 0);
 return {
 itemName: woItem.itemName,
 description: woItem.description || "",
 unit: woItem.unit || "",
 woQuantity: woQty,
 previousQuantity: prevQty,
 remainingQuantity: remaining,
 rate: num(woItem.rate),
 _existingItem: existingItem,
 };
 }).filter((it) => it.remainingQuantity > 0);

 setAvailableItems(allItems);

 // Pre-select items that have existing measurements
 const preSelectedKeys = allItems
 .filter((it) => it._existingItem)
 .map((it) => getItemKey(it));
 setSelectedItemKeys(preSelectedKeys);

 // Build items with measurements pre-filled
 const preItems = allItems
 .filter((it) => it._existingItem)
 .map((it) => {
 const existingItem = it._existingItem;
 const measurements =
 existingItem?.measurements?.length > 0
 ? existingItem.measurements.map((m) => ({
 location: m.location || "",
 nos: num(m.nos) || 1,
 length: num(m.length),
 breadth: num(m.breadth),
 height: num(m.height),
 remarks: m.remarks || "",
 }))
 : num(existingItem?.measuredQuantity) > 0
 ? [{ location: existingItem?.location || "", nos: 1, length: num(existingItem.measuredQuantity), breadth: 0, height: 0, remarks: "" }]
 : [emptyMeasurement()];

 const { _existingItem, ...rest } = it;
 return { ...rest, measurements };
 });

 setItems(preItems);
 }, [selectedVWO, existingJMR, measuredQuantityMap]);

 // Measurement helpers 
 const totalMeasured = (item) =>
 Number(item.measurements.reduce((s, m) => s + calcQty(m), 0).toFixed(4));

 const currentRemaining = (item) =>
 Number(Math.max(item.remainingQuantity - totalMeasured(item), 0).toFixed(4));

 const itemOptions = useMemo(() =>
 availableItems.map((item) => ({
 value: getItemKey(item),
 label: `${item.itemName}${item.description ? ` — ${item.description}` : ""} (Remaining: ${item.remainingQuantity} ${item.unit || ""})`,
 item,
 })),
 [availableItems]
 );

 const handleItemSelect = (opts) => {
 const selected = opts || [];
 setSelectedItemKeys(selected.map((o) => o.value));
 setItems((prev) => {
 const prevMap = new Map(prev.map((it) => [getItemKey(it), it]));
 return selected.map((o) => {
 const key = o.value;
 if (prevMap.has(key)) return prevMap.get(key);
 return { ...o.item, measurements: [emptyMeasurement()] };
 });
 });
 };

 const removeItem = (itemIdx) => {
 const key = getItemKey(items[itemIdx]);
 setSelectedItemKeys((prev) => prev.filter((k) => k !== key));
 setItems((prev) => prev.filter((_, i) => i !== itemIdx));
 };

 const updateMeasRow = (itemIdx, rowIdx, field, value) => {
 setItems((prev) =>
 prev.map((item, i) => {
 if (i !== itemIdx) return item;
 const measurements = item.measurements.map((m, j) => {
 if (j !== rowIdx) return m;
 let val = value;
 if (field === "length") {
 const currentTotal = item.measurements.reduce((s, r, ri) => ri === rowIdx ? s : s + calcQty(r), 0);
 const maxAllowed = Math.max(item.remainingQuantity - currentTotal, 0);
 val = Math.min(Math.max(num(value), 0), maxAllowed);
 } return { ...m, [field]: val };
 });
 return { ...item, measurements };
 })
 );
 };

 const addMeasRow = (itemIdx) => {
 setItems((prev) =>
 prev.map((item, i) =>
 i === itemIdx ? { ...item, measurements: [...item.measurements, emptyMeasurement()] } : item
 )
 );
 };

 const removeMeasRow = (itemIdx, rowIdx) => {
 setItems((prev) =>
 prev.map((item, i) => {
 if (i !== itemIdx) return item;
 const measurements = item.measurements.filter((_, j) => j !== rowIdx);
 return { ...item, measurements: measurements.length ? measurements : [emptyMeasurement()] };
 })
 );
 };

 // Submit 
 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!measurementDate) return swal("Error", "Measurement date is required", "error");

 const validItems = items.filter((item) => totalMeasured(item) > 0).map((item) => ({
 ...item,
 measurements: item.measurements.filter((m) => calcQty(m) > 0),
 }));
 if (!validItems.length) return swal("Error", "At least one item with measured quantity is required", "error");

 const overQty = validItems.find((item) => totalMeasured(item) > item.remainingQuantity);
 if (overQty) return swal("Error", `${overQty.itemName} measured quantity exceeds remaining quantity (${overQty.remainingQuantity})`, "error");

 setPendingFormData({ validItems });
 setShowSignModal(true);
 };

 const handleSignatureSave = async (sigFile, _stamp, _r) => {
 setShowSignModal(false);
 if (!pendingFormData) return;
 try {
 setSaving(true);
 const data = new FormData();
 data.append("vendorWorkOrder", existingJMR.vendorWorkOrder?._id || existingJMR.vendorWorkOrder);
 data.append("measurementDate", measurementDate);
 data.append("remarks", remarks);
 data.append("items", JSON.stringify(pendingFormData.validItems));
 if (jmrFile) data.append("jmrDocument", jmrFile);
 if (sigFile) data.append("submittedBySign", sigFile);

 const res = await api.put(`/vendor-jmrs/${id}`, data, { headers: { "Content-Type": "multipart/form-data" } });
 if (res.data.success) {
 swal("Success", "Vendor JMR updated successfully", "success");
 navigate("/vendor-jmr");
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to update Vendor JMR", "error");
 } finally {
 setSaving(false);
 setPendingFormData(null);
 }
 };

 if (loading) return <div className="text-center py-5"><Spinner /></div>;

 return (
 <Fragment>
 <PageTitle activeMenu="Edit Vendor JMR" motherMenu="Commercial" />
 <Form onSubmit={handleSubmit}>
 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">Edit Vendor JMR — {existingJMR?.jmrNumber}</Card.Title>
 <Button variant="light" size="sm" onClick={() => navigate("/vendor-jmr")}>Back</Button>
 </Card.Header>

 <Card.Body>
 <Row className="g-3 mb-4">
 <Col md={5}>
 <Form.Label>Vendor Work Order</Form.Label>
 <div className="form-control bg-light fw-bold">
 {selectedVWO?.woNumber || "—"}{selectedVWO?.title ? ` — ${selectedVWO.title}` : ""}
 </div>
 </Col>
 <Col md={3}>
 <Form.Label>Vendor</Form.Label>
 <div className="form-control bg-light fw-bold">
 {selectedVWO?.vendor?.name || existingJMR?.vendor?.name || "—"}
 </div>
 </Col>
 {/* <Col md={3}>
 <Form.Label>WO Value</Form.Label>
 <div className="form-control bg-light fw-bold text-primary">
 {Number(selectedVWO?.totalAmount || 0).toLocaleString("en-IN", { style: "currency", currency: "INR" })}
 </div>
 </Col> */}
 <Col md={4}>
 <Form.Label>Measurement Date <span className="text-danger">*</span></Form.Label>
 <Form.Control type="date" value={measurementDate} onChange={(e) => setMeasurementDate(e.target.value)} required />
 </Col>
 <Col md={4}>
 <Form.Label>Replace JMR Document</Form.Label>
 <Form.Control type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setJmrFile(e.target.files?.[0] || null)} />
 {existingJMR?.jmrDocument?.fileName && !jmrFile && (
 <Form.Text className="text-muted">Current: {existingJMR.jmrDocument.fileName}</Form.Text>
 )}
 </Col>
 <Col md={12}>
 <Form.Label>Remarks</Form.Label>
 <Form.Control as="textarea" rows={2} placeholder="Any notes or remarks..." value={remarks} onChange={(e) => setRemarks(e.target.value)} />
 </Col>
 </Row>

 {/* Item Selection */}
 <div className="border rounded p-3 mb-4">
 <Form.Label className="fw-bold mb-2">
 Select Items <span className="text-danger">*</span>
 {availableItems.length > 0 && (
 <span className="ms-2 text-muted fw-normal small">
 ({availableItems.length} item{availableItems.length !== 1 ? "s" : ""} available)
 </span>
 )}
 </Form.Label>
 <Select
 isMulti
 options={itemOptions}
 value={itemOptions.filter((o) => selectedItemKeys.includes(o.value))}
 onChange={handleItemSelect}
 placeholder="Select items to measure..."
 isDisabled={availableItems.length === 0}
 closeMenuOnSelect={false}
 classNamePrefix="react-select"
 noOptionsMessage={() => "All items fully measured"}
 />
 </div>

 {items.length === 0 && selectedItemKeys.length === 0 && availableItems.length > 0 && (
 <div className="text-center text-muted py-3 border rounded">
 Select items above to enter measurements.
 </div>
 )}

 {/* Items Table — same layout as screenshot */}
 {items.length > 0 && (
 <div className="table-responsive">
 <Table bordered className="align-middle">
 <thead className="table-light">
 <tr>
 <th style={{ width: 40 }}>#</th>
 <th>Item</th>
 <th style={{ width: 70 }}>Unit</th>
 <th style={{ width: 80 }}>WO Qty</th>
 <th style={{ width: 100 }}>Previously Measured</th>
 <th style={{ width: 90 }}>Remaining</th>
 <th style={{ width: 120 }}>Measured Qty</th>
 <th>Location</th>
 <th style={{ width: 36 }}></th>
 </tr>
 </thead>
 <tbody>
 {items.map((item, itemIdx) =>
 item.measurements.map((m, rowIdx) => (
 <tr key={`${itemIdx}-${rowIdx}`}>
 {rowIdx === 0 && (
 <>
 <td rowSpan={item.measurements.length + 1} className="text-center align-middle">
 {itemIdx + 1}
 </td>
 <td rowSpan={item.measurements.length + 1} className="align-middle">
 <strong>{item.itemName}</strong>
 {item.description && <div className="small text-muted">{item.description}</div>}
 </td>
 <td rowSpan={item.measurements.length + 1} className="text-center align-middle">
 {item.unit || "—"}
 </td>
 <td rowSpan={item.measurements.length + 1} className="text-center align-middle">
 {item.woQuantity}
 </td>
 <td rowSpan={item.measurements.length + 1} className="text-center align-middle text-danger">
 {item.previousQuantity}
 </td>
 <td rowSpan={item.measurements.length + 1} className="text-center align-middle fw-bold text-success">
 {currentRemaining(item)}
 </td>
 </>
 )}
 <td>
 <Form.Control
 type="number" size="sm" step="any" min="0"
 max={item.remainingQuantity}
 value={m.length}
 onChange={(e) => updateMeasRow(itemIdx, rowIdx, "length", e.target.value)}
 />
 </td>
 <td>
 <Form.Control
 as="textarea" rows={1} size="sm"
 style={{ resize: "vertical", minHeight: "32px" }}
 placeholder="Location..."
 value={m.location}
 onChange={(e) => updateMeasRow(itemIdx, rowIdx, "location", e.target.value)}
 />
 </td>
 <td className="text-center">
 <Button variant="outline-danger" size="sm" style={{ padding: "2px 6px" }}
 onClick={() => removeMeasRow(itemIdx, rowIdx)}>
 <i className="fa fa-trash"></i>
 </Button>
 </td>
 </tr>
 )).concat(
 <tr key={`${itemIdx}-footer`} style={{ background: "transparent" }}>
 <td colSpan={2} className="ps-2" style={{ background: "transparent" }}>
 <Button
 variant="link"
 size="sm"
 className="text-primary p-0 text-decoration-none fw-semibold"
 onClick={() => addMeasRow(itemIdx)}
 >
 <i className="fa fa-plus me-1"></i> Add Row
 </Button>
 </td>
 <td className="text-center fw-bold text-primary" style={{ background: "transparent" }}>
 Total: {totalMeasured(item)}
 {totalMeasured(item) > item.remainingQuantity && (
 <Badge bg="danger" className="ms-1">Over!</Badge>
 )}
 </td>
 </tr>
 )
 )}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>

 <Card.Footer className="text-end bg-white">
 <Button variant="secondary" className="me-2" onClick={() => navigate("/vendor-jmr")}>Cancel</Button>
 <Button type="submit" disabled={saving || !items.length}>
 {saving ? <><Spinner size="sm" className="me-2" />Saving...</> : "Update Vendor JMR"}
 </Button>
 </Card.Footer>
 </Card>
 </Form>

 <SignatureModal
 show={showSignModal}
 onHide={() => { setShowSignModal(false); setPendingFormData(null); }}
 onSave={handleSignatureSave}
 title="Submitted By — Signature Required"
 />
 </Fragment>
 );
};

export default VendorJMREdit;
